/**
 * This file is created automatically as part of the build process.
 * See ic-design/readme.md for more information.
 */

import { jsxs, jsx } from '@emotion/core/jsx-runtime';
import { forwardRef, createContext, useContext } from 'react';
import { internalSVGIcon, useTheme, combineStyles, InputBase, POSITION_Y_CENTER, toolingInputStyles, DollarIcon, PercentIcon, SearchIcon, spacing, CompositionXorItemsError, CompositionTooManyItemsError, CompositionOrderError, CompositionValidator, useAriaLabel, InformationIcon, unstableCoachmark, CompositionTooFewItemsError, ButtonBase, CheckboxBase, ArrowUpIcon, ArrowDownIcon, CompositionEngineItemType, CompositionInvalidChildError, blendColors, SheetDropdownDisclosureBase, OptionsIcon, useAriaLoading, SwitchBase, ApprovedIcon, IssueIcon, CloseIcon } from '@instacart/ids-core';
import { PopoverDisclosure } from 'reakit';
import { keyframes } from '@emotion/core';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __makeTemplateObject(cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
}

var SVGIcon$2 = internalSVGIcon.SVGIcon;
var getIconStyles = function (alignRight) {
    return ({
        icon: __assign(__assign({ position: 'absolute' }, (alignRight ? { right: 12 } : { left: 12 })), POSITION_Y_CENTER),
        iconInput: {
            position: 'relative',
            width: '100%',
        },
    });
};
var getInputStyles = function (theme, alignRight) { return (__assign(__assign({}, toolingInputStyles(theme)), { input: __assign(__assign({}, (alignRight ? { paddingRight: 36 } : { paddingLeft: 36 })), { '::-webkit-inner-spin-button, ::-webkit-outer-spin-button': {
            WebkitAppearance: 'none',
            margin: 0,
        }, '&[type="number"]': {
            MozAppearance: 'textfield',
        } }) })); };
var IconInputBase = forwardRef(function IconInputBase(_a, ref) {
    var id = _a.id, component = _a.component, styles = _a.styles, disabled = _a.disabled, _b = _a.alignRight, alignRight = _b === void 0 ? false : _b, rest = __rest(_a, ["id", "component", "styles", "disabled", "alignRight"]);
    var theme = useTheme();
    var _c = combineStyles(getIconStyles(alignRight), styles), icon = _c.icon, iconInput = _c.iconInput;
    var inputStyles = getInputStyles(theme, alignRight);
    var color = disabled ? 'systemGrayscale30' : 'systemGrayscale70';
    return (jsxs("div", __assign({ css: iconInput }, { children: [jsx(InputBase, __assign({ id: id, styles: inputStyles, ref: ref, "data-testid": "icon-input-base", disabled: disabled }, rest)), jsx(SVGIcon$2, { "aria-hidden": true, size: 16, component: component, css: icon, color: color })] })));
});
IconInputBase.displayName = 'IconInputBase';

var InputCurrency = forwardRef(function InputCurrency(_a, ref) {
    var id = _a.id, rest = __rest(_a, ["id"]);
    return (jsx(IconInputBase, __assign({ id: id, ref: ref, type: "number", component: jsx(DollarIcon, { "data-testid": "dollar-icon" }) }, rest)));
});
InputCurrency.displayName = 'InputCurrency';

var InputPercent = forwardRef(function InputPercent(_a, ref) {
    var id = _a.id, rest = __rest(_a, ["id"]);
    return (jsx(IconInputBase, __assign({ id: id, ref: ref, type: "number", component: jsx(PercentIcon, { "data-testid": "percent-icon" }) }, rest)));
});
InputPercent.displayName = 'InputPercent';

var InputSearch = forwardRef(function InputPercent(_a, ref) {
    var id = _a.id, rest = __rest(_a, ["id"]);
    return (jsx(IconInputBase, __assign({ id: id, ref: ref, type: "text", component: jsx(SearchIcon, { "data-testid": "search-icon" }), alignRight: true }, rest)));
});
InputSearch.displayName = 'InputSearch';

var getStyles$D = function (theme) { return toolingInputStyles(theme); };
var InputText = forwardRef(function InputText(props, ref) {
    var theme = useTheme();
    var styles = getStyles$D(theme);
    return jsx(InputBase, __assign({ styles: styles, ref: ref }, props));
});
InputText.displayName = 'InputText';

var getStyles$C = function (theme) {
    return ({
        text: __assign({ display: 'block', marginTop: spacing.s4 }, theme.typography.bodyMedium2),
        disabled: {
            color: theme.colors.systemGrayscale30,
        },
    });
};
var InputDescriptionBase = function (_a) {
    var styles = _a.styles, children = _a.children, disabled = _a.disabled, rest = __rest(_a, ["styles", "children", "disabled"]);
    var theme = useTheme();
    var _b = combineStyles(getStyles$C(theme), styles), text = _b.text, disabledStyles = _b.disabled;
    var css = [
        text,
        {
            '&.disabled': disabledStyles,
        },
    ];
    return (jsx("span", __assign({ css: css, className: disabled ? 'disabled' : undefined, "data-testid": "input-description-base" }, rest, { children: children })));
};
InputDescriptionBase.displayName = 'InputDescriptionBase';

var getStyles$B = function (theme) { return ({
    text: {
        color: theme.colors.systemDetrimentalDark,
    },
}); };
var InputDescriptionErrorText = function (props) {
    var theme = useTheme();
    var styles = getStyles$B(theme);
    return jsx(InputDescriptionBase, __assign({ styles: styles, "data-testid": "error-text" }, props));
};
InputDescriptionErrorText.displayName = 'InputDescriptionErrorText';

var getStyles$A = function (theme) { return ({
    text: {
        color: theme.colors.systemGrayscale50,
    },
}); };
var InputDescriptionHintText = function (props) {
    var theme = useTheme();
    var styles = getStyles$A(theme);
    return jsx(InputDescriptionBase, __assign({ styles: styles, "data-testid": "hint-text" }, props));
};
InputDescriptionHintText.displayName = 'InputDescriptionHintText';

var validateInputWrapperNumber = function (engine) {
    var expect = engine.expect, findAllByKind = engine.findAllByKind, flatIndexOfByKind = engine.flatIndexOfByKind;
    var inputTextIndex = flatIndexOfByKind(InputText);
    var inputPercentIndex = flatIndexOfByKind(InputPercent);
    var inputCurrencyIndex = flatIndexOfByKind(InputCurrency);
    var hintIndex = flatIndexOfByKind(InputDescriptionHintText);
    var errorIndex = flatIndexOfByKind(InputDescriptionErrorText);
    var foundInputTexts = findAllByKind(InputText);
    var foundInputPercent = findAllByKind(InputPercent);
    var foundInputCurrency = findAllByKind(InputCurrency);
    var foundHints = findAllByKind(InputDescriptionHintText);
    var foundErrors = findAllByKind(InputDescriptionErrorText);
    // It must contain only one input type: InputText|InputCurrency|InputPercent
    var inputIndicesFound = [inputTextIndex, inputPercentIndex, inputCurrencyIndex].filter(function (i) { return i > -1; }).length;
    if (inputIndicesFound > 1 || inputIndicesFound < 1) {
        throw new CompositionXorItemsError([InputText]);
    }
    // it may contain up to 1 InputText|InputCurrency|InputPercent at the most each
    // InputText
    try {
        expect(foundInputTexts).toHaveMaxLength(1);
    }
    catch (_a) {
        throw new CompositionTooManyItemsError(InputText, 'one at most');
    }
    // InputPercent
    try {
        expect(foundInputPercent).toHaveMaxLength(1);
    }
    catch (_b) {
        throw new CompositionTooManyItemsError(InputPercent, 'one at most');
    }
    // InputCurrency
    try {
        expect(foundInputCurrency).toHaveMaxLength(1);
    }
    catch (_c) {
        throw new CompositionTooManyItemsError(InputCurrency, 'one at most');
    }
    // It must contain only one sub text type: InputDescriptionHintText|InputDescriptionErrorText
    var descriptionIndicesFound = [hintIndex, errorIndex].filter(function (i) { return i > -1; }).length;
    if (descriptionIndicesFound > 1) {
        throw new CompositionXorItemsError([InputDescriptionHintText, InputDescriptionErrorText]);
    }
    // it may contain up to one InputDescriptionHintText/InputDescriptionErrorText at the most each
    try {
        expect(foundHints).toHaveMaxLength(1);
    }
    catch (_d) {
        throw new CompositionTooManyItemsError(InputDescriptionHintText, 'one at most');
    }
    // InputDescriptionErrorText
    try {
        expect(foundErrors).toHaveMaxLength(1);
    }
    catch (_e) {
        throw new CompositionTooManyItemsError(InputDescriptionErrorText, 'one at most');
    }
};
var validateInputWrapperOrder = function (engine) {
    var expect = engine.expect, getAllChildren = engine.getAllChildren, flatIndexOfByKind = engine.flatIndexOfByKind;
    var all = getAllChildren();
    var hintIndex = flatIndexOfByKind(InputDescriptionHintText);
    var errorIndex = flatIndexOfByKind(InputDescriptionErrorText);
    // If InputDescriptionHintText is provided, it must come last
    if (hintIndex > -1) {
        try {
            expect(hintIndex).toEqual(1);
        }
        catch (_a) {
            throw new CompositionOrderError(all[1], all[hintIndex]);
        }
    }
    // If InputDescriptionErrorText is provided, it must come last
    if (errorIndex > -1) {
        try {
            expect(errorIndex).toEqual(1);
        }
        catch (_b) {
            throw new CompositionOrderError(all[1], all[errorIndex]);
        }
    }
};

var getStyles$z = function () {
    return ({
        container: {
            width: '100%',
        },
    });
};
var validator$8 = new CompositionValidator([validateInputWrapperNumber, validateInputWrapperOrder]);
var InputWrapper = function (_a) {
    var styles = _a.styles, children = _a.children, rest = __rest(_a, ["styles", "children"]);
    var container = combineStyles(getStyles$z(), styles).container;
    validator$8.validate(children);
    return (jsx("span", __assign({ css: container }, rest, { "data-testid": "input-wrapper" }, { children: children })));
};
InputWrapper.displayName = 'InputWrapper';

var MAX_ICON_SIZE$1 = 24;
var TRANSITION_DURATION_MS = 200;
var getStyles$y = function (theme) {
    return ({
        disclosure: {
            border: 'none',
            padding: 0,
            margin: 0,
            cursor: 'pointer',
            touchAction: 'manipulation',
            position: 'relative',
            backgroundColor: 'transparent',
            marginLeft: 10,
            height: 38,
            width: 24,
            flexGrow: 0,
        },
        focusRing: {
            border: "2px solid ".concat(theme.colors.systemGrayscale70),
            width: MAX_ICON_SIZE$1 + 4,
            height: MAX_ICON_SIZE$1 + 4,
            borderRadius: '50%',
            content: '""',
            display: 'block',
            position: 'absolute',
            top: 2,
            left: -4,
        },
        icon: {
            transition: "all ".concat(TRANSITION_DURATION_MS, "ms ease-in-out"),
        },
        iconHover: {
            fill: theme.colors.systemGrayscale50,
        },
        hover: {},
        active: {},
        disabled: {
            cursor: 'default',
        },
    });
};
var InfoInputIcon = forwardRef(function InfoInputIcon(_a, ref) {
    var styles = _a.styles, description = _a.description, popover = _a.popover, rest = __rest(_a, ["styles", "description", "popover"]);
    var theme = useTheme();
    var _b = combineStyles(getStyles$y(theme), styles), icon = _b.icon, iconHover = _b.iconHover, disclosure = _b.disclosure, focusRing = _b.focusRing, active = _b.active, disabled = _b.disabled, hover = _b.hover;
    var disclosureCss = [
        disclosure,
        {
            '&:focus': {
                outline: 'none',
            },
            '&.focus-visible::after': focusRing,
            '&:hover': hover,
            '&:hover svg': iconHover,
            '&:active:not(:disabled)': active,
            '&:disabled': disabled,
        },
    ];
    var svgCss = [icon, { '&:hover': iconHover }];
    var ariaLabel = useAriaLabel({ description: description });
    return (jsx(PopoverDisclosure, __assign({}, ariaLabel, { ref: ref, css: disclosureCss }, popover, rest, { children: jsx(InformationIcon, { size: MAX_ICON_SIZE$1, color: "systemGrayscale30", css: svgCss, "data-testid": "input-info-icon" }) })));
});
InfoInputIcon.displayName = 'InfoInputIcon';

var index$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    InfoInputIcon: InfoInputIcon
});

var DEFAULT_OPTIONAL_TEXT = 'optional';
var LABEL_WIDTH = 144;
var getStyles$x = function (theme) {
    return ({
        label: __assign({ color: theme.colors.systemGrayscale70, textAlign: 'right', marginRight: spacing.s12, width: LABEL_WIDTH, flexShrink: 0, display: 'flex', height: '38px', alignItems: 'center', justifyContent: 'flex-end' }, theme.typography.bodyMedium1),
        content: {},
        optional: __assign({}, theme.typography.bodyMedium2),
        disabled: {
            color: theme.colors.systemGrayscale30,
        },
    });
};
var LabelBase = function (_a) {
    var styles = _a.styles, children = _a.children, _b = _a.isOptional, isOptional = _b === void 0 ? false : _b, _c = _a.optionalText, optionalText = _c === void 0 ? DEFAULT_OPTIONAL_TEXT : _c, htmlFor = _a.htmlFor, disabled = _a.disabled, rest = __rest(_a, ["styles", "children", "isOptional", "optionalText", "htmlFor", "disabled"]);
    var theme = useTheme();
    var _d = combineStyles(getStyles$x(theme), styles), label = _d.label, content = _d.content, optional = _d.optional, disabledStyles = _d.disabled;
    var css = [
        label,
        {
            '&.disabled': disabledStyles,
        },
    ];
    var optionalString = optionalText && " (".concat(optionalText.trim(), ")");
    return (jsx("label", __assign({ css: css, htmlFor: htmlFor, className: disabled ? 'disabled' : undefined, "data-testid": "label-base" }, rest, { children: jsxs("span", __assign({ css: content }, { children: [children, isOptional ? jsx("span", __assign({ css: optional }, { children: optionalString })) : null] })) })));
};

var Label = function (props) {
    return jsx(LabelBase, __assign({}, props));
};
Label.displayName = 'Label';

var Coachmark = unstableCoachmark.Coachmark;
var validateFieldBase = function (engine) {
    var expect = engine.expect, findAllByKind = engine.findAllByKind, flatIndexOfByKind = engine.flatIndexOfByKind, getAll = engine.getAll;
    var all = getAll();
    var wrapperIndex = flatIndexOfByKind(InputWrapper);
    var iconIndex = flatIndexOfByKind(InfoInputIcon);
    var coachmarkIndex = flatIndexOfByKind(Coachmark);
    var labelIndex = flatIndexOfByKind(Label);
    var foundWrappers = findAllByKind(InputWrapper);
    var foundIcons = findAllByKind(InfoInputIcon);
    var foundCoachmarks = findAllByKind(Coachmark);
    var foundLabels = findAllByKind(Label);
    // It must contain only one Label
    try {
        expect(foundLabels).toHaveLength(1);
    }
    catch (_a) {
        if (foundLabels.length > 1) {
            throw new CompositionTooManyItemsError(Label, 'one at most');
        }
        else if (foundLabels.length < 1) {
            throw new CompositionTooFewItemsError(Label, 'one at most');
        }
    }
    // It must contain only one InputWrapper
    try {
        expect(foundWrappers).toHaveLength(1);
    }
    catch (_b) {
        if (foundWrappers.length > 1) {
            throw new CompositionTooManyItemsError(InputWrapper, 'one at most');
        }
        else if (foundWrappers.length < 1) {
            throw new CompositionTooFewItemsError(InputWrapper, 'one at most');
        }
    }
    // it may contain up to one InfoInputIcon at the most
    try {
        expect(foundIcons).toHaveMaxLength(1);
    }
    catch (_c) {
        throw new CompositionTooManyItemsError(InfoInputIcon, 'one at most');
    }
    // it may contain up to one Coachmark at the most
    try {
        expect(foundCoachmarks).toHaveMaxLength(1);
    }
    catch (_d) {
        throw new CompositionTooManyItemsError(Coachmark, 'one at most');
    }
    // if InfoInputIcon is provided, Coachmark must be too
    var indicesFound = [iconIndex, coachmarkIndex].filter(function (i) { return i > -1; }).length;
    if (iconIndex > -1 || coachmarkIndex > -1) {
        try {
            expect(indicesFound).toEqual(2);
        }
        catch (_e) {
            if (iconIndex < 0) {
                throw new CompositionTooFewItemsError(InfoInputIcon, 'exactly one');
            }
            else if (coachmarkIndex < 0) {
                throw new CompositionTooFewItemsError(Coachmark, 'exactly one');
            }
        }
    }
    // Label must come before InputWrapper
    if (labelIndex > -1 && wrapperIndex > -1) {
        try {
            expect(labelIndex).toBeLessThan(wrapperIndex);
        }
        catch (_f) {
            throw new CompositionOrderError(all[labelIndex], all[wrapperIndex]);
        }
    }
    // InputWrapper must come before InfoInputIcon
    if (wrapperIndex > -1 && iconIndex > -1) {
        try {
            expect(wrapperIndex).toBeLessThan(iconIndex);
        }
        catch (_g) {
            throw new CompositionOrderError(all[wrapperIndex], all[iconIndex]);
        }
    }
};

var getStyles$w = function () {
    return ({
        field: {
            display: 'flex',
            padding: "".concat(spacing.s12, "px 0px"),
        },
    });
};
var validator$7 = new CompositionValidator([validateFieldBase]);
var FieldBase = forwardRef(function FieldBase(_a, ref) {
    var styles = _a.styles, children = _a.children, rest = __rest(_a, ["styles", "children"]);
    var field = combineStyles(getStyles$w(), styles).field;
    validator$7.validate(children);
    return (jsx("div", __assign({ css: field, ref: ref }, rest, { children: children })));
});
FieldBase.displayName = 'FieldBase';

var ACTION_HEIGHT = 32;
var OPTICAL_ALIGNED_PADDING_TOP = 6;
var getStyles$v = function (theme) { return ({
    button: __assign({ height: ACTION_HEIGHT, padding: "".concat(OPTICAL_ALIGNED_PADDING_TOP, "px ").concat(spacing.s12, "px ").concat(spacing.s8, "px"), backgroundColor: theme.colors.systemGrayscale10, color: theme.colors.systemGrayscale70, borderRadius: theme.radius.r8 }, theme.typography.bodySmall1),
    active: {
        backgroundColor: theme.colors.systemGrayscale20,
    },
    focusRing: {
        borderRadius: theme.radius.r8,
    },
    disabled: {
        backgroundColor: theme.colors.systemGrayscale10,
        color: theme.colors.systemGrayscale30,
        cursor: 'not-allowed',
    },
}); };
var CellButtonBase = forwardRef(function CellButtonBase(_a, ref) {
    var rest = __rest(_a, []);
    var theme = useTheme();
    var styles = getStyles$v(theme);
    return jsx(ButtonBase, __assign({}, rest, { styles: styles, ref: ref, type: "button" }));
});
CellButtonBase.displayName = 'CellButtonBase';

var getStyles$u = function () {
    return ({
        image: {
            display: 'block',
            width: spacing.s24,
            height: spacing.s24,
        },
    });
};
var CellImageBase = function (props) {
    var alt = props.alt, styles = props.styles, rest = __rest(props, ["alt", "styles"]);
    var image = combineStyles(getStyles$u(), styles).image;
    return jsx("img", __assign({}, rest, { alt: alt, css: image }));
};
CellImageBase.displayName = 'CellImageBase';

var Align;
(function (Align) {
    Align["LEFT"] = "left";
    Align["RIGHT"] = "right";
})(Align || (Align = {}));

var MAX_WIDTH_PX$2 = 384;
var HEIGHT_PX = 38;
var getAlignmentStyles$2 = function (alignment) {
    switch (alignment) {
        case Align.RIGHT:
            return {
                paddingLeft: "".concat(spacing.s8, "px"),
                justifyContent: 'flex-end',
                textAlign: Align.RIGHT,
            };
        case Align.LEFT:
        default:
            return {
                textAlign: Align.LEFT,
            };
    }
};
var getStyles$t = function (theme, alignment, gutterPx) {
    return ({
        cell: __assign({ maxWidth: MAX_WIDTH_PX$2, height: HEIGHT_PX, backgroundColor: theme.colors.systemGrayscale00, color: theme.colors.systemGrayscale70, boxShadow: "inset 0px 1px 0px ".concat(theme.colors.systemGrayscale10) }, theme.typography.bodyMedium2),
        content: __assign(__assign({ display: 'flex', alignItems: 'center', padding: "7px ".concat(spacing.s16, "px 9px 0px") }, getAlignmentStyles$2(alignment)), { '&': {
                marginLeft: "-".concat(gutterPx, "px"),
            }, '& > *': {
                // Necessary blunt tool: Using !important flag to override any potential specificity issues
                // e.g., ButtonBase explicitly sets margin: 0; and clashes w/ this rule as a result
                marginLeft: "".concat(gutterPx, "px !important"),
            } }),
    });
};
var CellBase = function (_a) {
    var styles = _a.styles, overrideAsHeader = _a.overrideAsHeader, _b = _a.alignment, alignment = _b === void 0 ? Align.LEFT : _b, _c = _a.gutterPx, gutterPx = _c === void 0 ? spacing.s8 : _c, children = _a.children, rest = __rest(_a, ["styles", "overrideAsHeader", "alignment", "gutterPx", "children"]);
    var theme = useTheme();
    var El = overrideAsHeader ? 'th' : 'td';
    var _d = combineStyles(getStyles$t(theme, alignment, gutterPx), styles), cell = _d.cell, content = _d.content;
    var scope = overrideAsHeader ? { scope: 'row' } : {};
    return (jsx(El, __assign({ css: cell }, scope, rest, { children: jsx("div", __assign({ css: content, "data-testid": "table-cell-base-content" }, { children: children })) })));
};
CellBase.displayName = 'CellBase';

var getStyles$s = function () {
    return ({
        container: {
            marginTop: '-3px',
            marginBottom: '-3px',
            marginRight: 0,
        },
    });
};
var HeaderCheckbox = forwardRef(function HeaderCheckbox(props, ref) {
    var styles = getStyles$s();
    return jsx(CheckboxBase, __assign({}, props, { styles: styles, ref: ref }));
});
HeaderCheckbox.displayName = 'HeaderCheckbox';

var SVGIcon$1 = internalSVGIcon.SVGIcon;
var MAX_ICON_SIZE = 24;
var getStyles$r = function () {
    return ({
        icon: {
            marginRight: '-2px',
            flexShrink: 0,
        },
    });
};
var CellIcon = function (_a) {
    var alt = _a.alt, labelledBy = _a.labelledBy, rest = __rest(_a, ["alt", "labelledBy"]);
    var describedBy = useAriaLabel({ description: alt, relatedIds: labelledBy });
    var icon = getStyles$r().icon;
    return (jsx(SVGIcon$1, __assign({ size: MAX_ICON_SIZE, "data-testid": "cell-icon" }, rest, describedBy, { css: icon })));
};
CellIcon.displayName = 'CellIcon';

var getStyles$q = function () {
    return ({
        icon: {
            margin: '-3px 0',
        },
    });
};
var HeaderIcon = function (_a) {
    var alt = _a.alt, rest = __rest(_a, ["alt"]);
    var icon = getStyles$q().icon;
    return jsx(CellIcon, __assign({ css: icon, alt: alt }, rest));
};

var MAX_WIDTH_PX$1 = 340;
var getStyles$p = function () {
    return ({
        text: {
            display: 'block',
            maxWidth: MAX_WIDTH_PX$1,
            textOverflow: 'ellipsis',
            overflowX: 'hidden',
        },
    });
};
var CellText = function (_a) {
    var styles = _a.styles, children = _a.children, rest = __rest(_a, ["styles", "children"]);
    var text = combineStyles(getStyles$p(), styles).text;
    return (jsx("span", __assign({ css: text, "data-testid": "cell-text" }, rest, { children: children })));
};
CellText.displayName = 'CellText';

var getTextStyles$1 = function (theme) {
    return ({
        text: __assign({}, theme.typography.bodySmall2),
    });
};
var HeaderSubtitle = function (_a) {
    var children = _a.children;
    var theme = useTheme();
    var textStyles = getTextStyles$1(theme);
    return jsx(CellText, __assign({ styles: textStyles }, { children: children }));
};
HeaderSubtitle.displayName = 'HeaderSubtitle';

var getTextStyles = function (theme) {
    return ({
        text: __assign(__assign({}, theme.typography.bodyMedium1), { maxWidth: '320px' }),
    });
};
var HeaderTitleText = function (props) {
    var theme = useTheme();
    var textStyles = getTextStyles(theme);
    return jsx(CellText, __assign({ styles: textStyles }, props));
};
HeaderTitleText.displayName = 'HeaderTitleText';

var SVGIcon = internalSVGIcon.SVGIcon;
var ICON_SIZE = "".concat(spacing.s16, "px");
var SortState;
(function (SortState) {
    SortState["ASC"] = "ascending";
    SortState["DESC"] = "descending";
    SortState["DEFAULT"] = "default";
})(SortState || (SortState = {}));
var getStyles$o = function (sortState) {
    return ({
        icon: {
            visibility: sortState === SortState.DEFAULT ? 'hidden' : 'visible',
        },
    });
};
var HeaderSortIcon = function (_a) {
    var sortState = _a.sortState, styles = _a.styles, rest = __rest(_a, ["sortState", "styles"]);
    var icon = combineStyles(getStyles$o(sortState), styles).icon;
    var El = sortState === SortState.ASC ? ArrowUpIcon : ArrowDownIcon;
    var testId = "sort-icon".concat(sortState ? "-".concat(sortState) : '');
    return (jsx(SVGIcon, __assign({ "aria-hidden": true, component: jsx(El, { size: ICON_SIZE }), "data-testid": testId, css: icon }, rest)));
};

var validateHeaderTitle = function (engine) {
    var expect = engine.expect, findAllByKind = engine.findAllByKind, flatIndexOfByKind = engine.flatIndexOfByKind, childIndexOfByItemType = engine.childIndexOfByItemType, getAllChildren = engine.getAllChildren;
    var all = getAllChildren();
    var unwrappedTextIndex = childIndexOfByItemType(CompositionEngineItemType.TextContent);
    var subTitleIndex = flatIndexOfByKind(HeaderSubtitle);
    var sortIconIndex = flatIndexOfByKind(HeaderSortIcon);
    var iconIndex = flatIndexOfByKind(HeaderIcon);
    var checkboxIndex = flatIndexOfByKind(HeaderCheckbox);
    var foundTextTitles = findAllByKind(HeaderTitleText);
    // it must contain one HeaderTitleText
    try {
        expect(foundTextTitles).toHaveLength(1);
    }
    catch (_a) {
        var addtl = 'exactly one';
        if (foundTextTitles.length > 1) {
            throw new CompositionTooManyItemsError(HeaderTitleText, addtl);
        }
        else {
            throw new CompositionTooFewItemsError(HeaderTitleText, addtl);
        }
    }
    // it does not support unwrapped text
    if (unwrappedTextIndex > -1) {
        throw new CompositionInvalidChildError(all[unwrappedTextIndex], 'text must be wrapped in HeaderTitleText or HeaderSubtitleText');
    }
    // it must not be passed HeaderSortIcon explicitly
    if (sortIconIndex > -1) {
        throw new CompositionInvalidChildError(all[sortIconIndex], 'sort is handled internally by HeaderTitle via a sortState prop, please do not provide HeaderSortIcon directly');
    }
    // it cannot contain any other compositional header cell elements
    if (iconIndex > -1) {
        throw new CompositionTooManyItemsError(HeaderIcon, 'exactly zero');
    }
    if (checkboxIndex > -1) {
        throw new CompositionTooManyItemsError(HeaderCheckbox, 'exactly zero');
    }
    if (subTitleIndex > -1) {
        throw new CompositionTooManyItemsError(HeaderSubtitle, 'exactly zero');
    }
};

var getAlignmentStyles$1 = function (alignment) {
    return alignment === Align.RIGHT ? { container: { flexDirection: 'row-reverse' } } : {};
};
var getStyles$n = function () {
    return ({
        container: {
            display: 'flex',
            alignItems: 'center',
            '&': {
                marginLeft: "-".concat(spacing.s8, "px"),
            },
            '& > *': {
                marginLeft: spacing.s8,
            },
        },
    });
};
var validator$6 = new CompositionValidator([validateHeaderTitle]);
var HeaderTitle = forwardRef(function HeaderTitle(_a, ref) {
    var styles = _a.styles, _b = _a.alignment, alignment = _b === void 0 ? Align.LEFT : _b, children = _a.children, sortState = _a.sortState, rest = __rest(_a, ["styles", "alignment", "children", "sortState"]);
    var additionalStyles = __assign(__assign({}, getAlignmentStyles$1(alignment)), styles);
    var container = combineStyles(getStyles$n(), additionalStyles).container;
    validator$6.validate(children);
    return (jsxs("div", __assign({ css: container }, rest, { ref: ref, "data-testid": "header-title" }, { children: [children, sortState && jsx(HeaderSortIcon, { sortState: sortState })] })));
});
HeaderTitle.displayName = 'HeaderTitle';

var validateHeaderCellBaseNumber = function (engine) {
    var expect = engine.expect, findAllByKind = engine.findAllByKind, flatIndexOfByKind = engine.flatIndexOfByKind, getAllChildren = engine.getAllChildren;
    var all = getAllChildren();
    var titleIndex = flatIndexOfByKind(HeaderTitle);
    var subTitleIndex = flatIndexOfByKind(HeaderSubtitle);
    var iconIndex = flatIndexOfByKind(HeaderIcon);
    var checkboxIndex = flatIndexOfByKind(HeaderCheckbox);
    var foundTitles = findAllByKind(HeaderTitle);
    var foundSubtitles = findAllByKind(HeaderSubtitle);
    var foundIcons = findAllByKind(HeaderIcon);
    var foundCheckboxes = findAllByKind(HeaderCheckbox);
    // only one of HeaderTitle/HeaderIcon/HeaderCheckbox may be used
    var indicesFound = [titleIndex, iconIndex, checkboxIndex].filter(function (i) { return i > -1; }).length;
    if (indicesFound > 1) {
        throw new CompositionXorItemsError([HeaderTitle, HeaderIcon, HeaderCheckbox]);
    }
    // it may contain up to one HeaderTitles/HeaderSubtitles/HeaderIcons/HeaderCheckboxes at the most each
    // HeaderTitle
    try {
        expect(foundTitles).toHaveMaxLength(1);
    }
    catch (_a) {
        throw new CompositionTooManyItemsError(HeaderTitle, 'one at most');
    }
    // HeaderSubtitle
    try {
        expect(foundSubtitles).toHaveMaxLength(1);
    }
    catch (_b) {
        throw new CompositionTooManyItemsError(HeaderSubtitle, 'one at most');
    }
    // HeaderIcon
    try {
        expect(foundIcons).toHaveMaxLength(1);
    }
    catch (_c) {
        throw new CompositionTooManyItemsError(HeaderIcon, 'one at most');
    }
    // HeaderCheckbox
    try {
        expect(foundCheckboxes).toHaveMaxLength(1);
    }
    catch (_d) {
        throw new CompositionTooManyItemsError(HeaderCheckbox, 'one at most');
    }
    // if HeaderSubtitle is provided, HeaderTitle must also be
    if (subTitleIndex > -1 && titleIndex < 0) {
        throw new CompositionTooFewItemsError(HeaderTitle, 'exactly one');
    }
    // HeaderIcon and HeaderCheckbox must have no siblings
    // HeaderIcon
    if (iconIndex > -1 && all.length > 1) {
        throw new CompositionTooManyItemsError(HeaderIcon, 'exactly zero siblings');
    }
    // HeaderCheckbox
    if (iconIndex > -1 && all.length > 1) {
        throw new CompositionTooManyItemsError(HeaderCheckbox, 'exactly zero siblings');
    }
};
var validateHeaderCellBaseOrder = function (engine) {
    var expect = engine.expect, getAll = engine.getAll, flatIndexOfByKind = engine.flatIndexOfByKind;
    var all = getAll();
    var titleIndex = flatIndexOfByKind(HeaderTitle);
    var subTitleIndex = flatIndexOfByKind(HeaderSubtitle);
    // If HeaderSubtitle is provided, it must come before HeaderTitle
    if (titleIndex > -1 && subTitleIndex > -1) {
        try {
            expect(titleIndex).toBeLessThan(subTitleIndex);
        }
        catch (_a) {
            throw new CompositionOrderError(all[titleIndex], all[subTitleIndex]);
        }
    }
};

var MAX_WIDTH_PX = 384;
var MIN_HEIGHT_PX = 44;
var getAlignmentStyles = function (alignment) {
    switch (alignment) {
        case Align.RIGHT:
            return {
                paddingLeft: "".concat(spacing.s16, "px"),
                textAlign: Align.RIGHT,
            };
        case Align.LEFT:
        default:
            return {
                textAlign: Align.LEFT,
            };
    }
};
var getContainerStyles = function (theme, alignment) { return ({
    margin: '11px 0 13px',
    backgroundColor: theme.colors.systemGrayscale00,
    color: theme.colors.systemGrayscale70,
    textAlign: alignment,
    width: '100%',
}); };
var getStyles$m = function (theme, alignment) {
    return ({
        cell: __assign({ maxWidth: MAX_WIDTH_PX, height: MIN_HEIGHT_PX, backgroundColor: theme.colors.systemGrayscale00, color: theme.colors.systemGrayscale70, boxShadow: "inset 0px 1px 0px ".concat(theme.colors.systemGrayscale20), padding: "0px ".concat(spacing.s16, "px 0px 0px") }, getAlignmentStyles(alignment)),
        container: getContainerStyles(theme, alignment),
    });
};
var getButtonStyles = function (theme, alignment) {
    return ({ button: getContainerStyles(theme, alignment) });
};
var validator$5 = new CompositionValidator([
    validateHeaderCellBaseNumber,
    validateHeaderCellBaseOrder,
]);
var HeaderCellBase = forwardRef(function HeaderCellBase(_a, ref) {
    var styles = _a.styles, _b = _a.alignment, alignment = _b === void 0 ? Align.LEFT : _b, onClick = _a.onClick, description = _a.description, children = _a.children, rest = __rest(_a, ["styles", "alignment", "onClick", "description", "children"]);
    var theme = useTheme();
    var _c = combineStyles(getStyles$m(theme, alignment), styles), cell = _c.cell, container = _c.container;
    var buttonStyles = getButtonStyles(theme, alignment);
    var ariaLabel = useAriaLabel({ description: description });
    var elem = onClick ? (jsx(ButtonBase, __assign({ ref: ref, styles: buttonStyles, onClick: onClick }, { children: children }))) : (jsx("div", __assign({ css: container }, { children: children })));
    validator$5.validate(children);
    return (jsx("th", __assign({ css: cell, scope: "col" }, ariaLabel, rest, { children: elem })));
});
HeaderCellBase.displayName = 'HeaderCellBase';

var getStyles$l = function (theme) { return ({
    button: {
        backgroundColor: theme.colors.systemGrayscale10,
        borderRadius: theme.radius.r8,
    },
    content: {},
    hover: {},
    active: {},
    disabled: {},
    focusRing: {},
}); };
var IconButtonBase = forwardRef(function IconButtonBase(_a, ref) {
    var Icon = _a.icon, color = _a.color, disabled = _a.disabled, accessibleLabel = _a.accessibleLabel, _b = _a.overrideStyles, overrideStyles = _b === void 0 ? {} : _b, props = __rest(_a, ["icon", "color", "disabled", "accessibleLabel", "overrideStyles"]);
    var theme = useTheme();
    var baseStyles = getStyles$l(theme);
    var styles = overrideStyles ? combineStyles(baseStyles, overrideStyles) : baseStyles;
    return (jsx(ButtonBase, __assign({ "aria-label": accessibleLabel }, props, { styles: styles, ref: ref }, { children: jsx(Icon, { color: disabled ? 'systemGrayscale30' : color, size: 20 }) })));
});

/*
  This wrapper was created due to test and browser console warnings and must be
  used for a strictly internal purpose for table data or header cells.
*/
var TableWrapper = function (_a) {
    var children = _a.children, styles = _a.styles;
    return (jsx("table", __assign({ css: styles }, { children: jsx("tbody", { children: jsx("tr", { children: children }) }) })));
};

function useButtonStyles(_a) {
    var primaryColor = _a.primaryColor, activeColor = _a.activeColor, textColor = _a.textColor;
    var theme = useTheme();
    return {
        button: {
            backgroundColor: theme.colors[primaryColor],
            color: theme.colors[textColor],
            borderRadius: theme.radius.r8,
            padding: "".concat(spacing.s8, "px ").concat(spacing.s12, "px"),
            minWidth: 166,
            height: 40,
        },
        content: __assign({}, theme.typography.bodyMedium1),
        focusRing: {
            borderRadius: theme.radius.r8,
        },
        hover: {
            backgroundColor: blendColors(theme.colors[primaryColor], theme.colors[activeColor], 51),
        },
        active: {
            backgroundColor: theme.colors[activeColor],
        },
        disabled: {
            backgroundColor: theme.colors.systemGrayscale10,
            color: theme.colors.systemGrayscale30,
        },
    };
}

function useStyles$2() {
    var styles = useButtonStyles({
        primaryColor: 'systemGrayscale10',
        activeColor: 'systemGrayscale20',
        textColor: 'systemGrayscale00',
    });
    return __assign(__assign({}, styles), { button: {
            padding: 0,
            width: 40,
            height: 40,
            minWidth: 'unset',
            lineHeight: 1,
        }, content: {} });
}
var IconButton = forwardRef(function IconButton(props, ref) {
    var styles = useStyles$2();
    return jsx(IconButtonBase, __assign({}, props, { overrideStyles: styles, ref: ref }));
});

var Field = forwardRef(function Field(props, ref) {
    return jsx(FieldBase, __assign({}, props, { ref: ref }));
});
Field.displayName = 'Field';

var PrimaryButton = forwardRef(function PrimaryButton(props, ref) {
    var styles = useButtonStyles({
        primaryColor: 'brandPrimaryRegular',
        activeColor: 'brandPrimaryDark',
        textColor: 'systemGrayscale00',
    });
    return jsx(ButtonBase, __assign({}, props, { styles: styles, ref: ref }));
});

var SecondaryButton = forwardRef(function SecondaryButton(props, ref) {
    var styles = useButtonStyles({
        primaryColor: 'systemGrayscale10',
        activeColor: 'systemGrayscale20',
        textColor: 'systemGrayscale70',
    });
    return jsx(ButtonBase, __assign({}, props, { styles: styles, ref: ref }));
});

var CellButton = forwardRef(function CellButton(props, ref) {
    return jsx(CellButtonBase, __assign({}, props, { ref: ref }));
});
CellButton.displayName = 'CellButton';

var getStyles$k = function (theme) {
    return ({
        button: {
            height: 32,
            padding: "8px 12px",
            backgroundColor: theme.colors.systemGrayscale10,
            color: theme.colors.systemGrayscale70,
            borderRadius: theme.radius.r8,
        },
        focusRing: {
            borderRadius: theme.radius.r8,
        },
    });
};
var CellOverflowButton = forwardRef(function CellOverflowButton(_a, ref) {
    var description = _a.description, rest = __rest(_a, ["description"]);
    var theme = useTheme();
    var styles = getStyles$k(theme);
    var ariaLabel = useAriaLabel({ description: description });
    return (jsx(SheetDropdownDisclosureBase, __assign({ styles: styles }, rest, ariaLabel, { ref: ref }, { children: jsx(OptionsIcon, { size: 16 }) })));
});
CellOverflowButton.displayName = 'CellOverflowButton';

var validateActionCell = function (engine) {
    var expect = engine.expect, getAll = engine.getAll, findAllByKind = engine.findAllByKind, flatIndexOfByKind = engine.flatIndexOfByKind, flatLastIndexOfByKind = engine.flatLastIndexOfByKind;
    var all = getAll();
    var buttonIndex = flatIndexOfByKind(CellButton);
    var overflowIndex = flatLastIndexOfByKind(CellOverflowButton);
    // ActionCell can accept up to 2 CellButtons
    var foundButtons = findAllByKind(CellButton);
    if (buttonIndex > -1) {
        try {
            expect(foundButtons).toHaveMaxLength(2);
        }
        catch (_a) {
            throw new CompositionTooManyItemsError(CellButton, 'two at most');
        }
    }
    // ActionCell can accept up to 1 CellOverflowButton
    if (overflowIndex > -1) {
        var foundOverflows = findAllByKind(CellOverflowButton);
        try {
            expect(foundOverflows).toHaveMaxLength(1);
        }
        catch (_b) {
            throw new CompositionTooManyItemsError(CellOverflowButton, 'one at most');
        }
    }
    // ActionCell can accept up to 1 CellButtons if CellOverflowButton is provided
    if (overflowIndex > -1) {
        try {
            expect(foundButtons).toHaveMaxLength(1);
        }
        catch (_c) {
            throw new CompositionTooManyItemsError(CellButton, 'one at most when using TableActionOverflow');
        }
    }
    // if CellOverflowButton is provided, it must come after CellButton
    if (overflowIndex > -1) {
        try {
            expect(buttonIndex).toBeLessThan(overflowIndex);
        }
        catch (_d) {
            throw new CompositionOrderError(all[overflowIndex], all[buttonIndex]);
        }
    }
};

var ACTION_CELL_GUTTER = spacing.s4;
var getStyles$j = function (alignment) { return ({
    content: {
        paddingLeft: alignment === Align.RIGHT ? spacing.s16 : 0,
        paddingRight: alignment === Align.LEFT ? spacing.s16 : 0,
        paddingTop: spacing.s4,
        paddingBottom: spacing.s4,
    },
}); };
var validator$4 = new CompositionValidator([validateActionCell]);
var ActionCell = function (props) {
    var children = props.children, alignment = props.alignment;
    var styles = getStyles$j(alignment);
    validator$4.validate(children);
    return (jsx(CellBase, __assign({ styles: styles, gutterPx: ACTION_CELL_GUTTER }, props, { children: children })));
};
ActionCell.displayName = 'ActionCell';

var getStyles$i = function (theme) {
    return ({
        a: {
            color: theme.colors.systemGrayscale70,
        },
    });
};
var CellLink = forwardRef(function CellLink(_a, ref) {
    var children = _a.children, styles = _a.styles, href = _a.href, title = _a.title, rest = __rest(_a, ["children", "styles", "href", "title"]);
    var theme = useTheme();
    var a = combineStyles(getStyles$i(theme), styles).a;
    return (jsx("a", __assign({ css: a, href: href, ref: ref }, rest, { children: jsx(CellText, __assign({ title: title }, { children: children })) })));
});
CellLink.displayName = 'CellLink';

var getStyles$h = function (theme) { return ({
    image: {
        width: spacing.s24,
        height: spacing.s24,
        borderRadius: theme.radius.r4,
    },
}); };
var CellImageSmall = function (props) {
    var theme = useTheme();
    var styles = getStyles$h(theme);
    return jsx(CellImageBase, __assign({ styles: styles }, props));
};

var getStyles$g = function (theme) { return ({
    image: {
        width: spacing.s40,
        height: spacing.s40,
        borderRadius: theme.radius.r8,
    },
}); };
var CellImageLarge = function (props) {
    var theme = useTheme();
    var styles = getStyles$g(theme);
    return jsx(CellImageBase, __assign({ styles: styles }, props));
};

var TextContent = CompositionEngineItemType.TextContent;
var validateContentCellNumber = function (engine) {
    var getAll = engine.getAll, expect = engine.expect, findAllByKind = engine.findAllByKind, flatIndexOfByKind = engine.flatIndexOfByKind, childIndexOfByItemType = engine.childIndexOfByItemType;
    var all = getAll();
    var textIndex = flatIndexOfByKind(CellText);
    var unwrappedTextIndex = childIndexOfByItemType(TextContent);
    var linkIndex = flatIndexOfByKind(CellLink);
    var iconIndex = flatIndexOfByKind(CellIcon);
    var baseImageIndex = flatIndexOfByKind(CellImageBase);
    var smallImageIndex = flatIndexOfByKind(CellImageSmall);
    var largeImageIndex = flatIndexOfByKind(CellImageLarge);
    var foundTexts = findAllByKind(CellText);
    var foundLinks = findAllByKind(CellLink);
    var foundIcons = findAllByKind(CellIcon);
    var foundBaseImages = findAllByKind(CellImageBase);
    var foundSmallImages = findAllByKind(CellImageSmall);
    var foundLargeImages = findAllByKind(CellImageLarge);
    // only one graphic type may be used
    var graphicIndicesFound = [iconIndex, baseImageIndex, smallImageIndex, largeImageIndex].filter(function (i) { return i > -1; }).length;
    if (graphicIndicesFound > 1) {
        throw new CompositionXorItemsError([CellImageBase, CellImageSmall, CellImageLarge, CellIcon]);
    }
    // only one of types CellText or CellIndex may be used
    var textIndicesFound = [textIndex, linkIndex].filter(function (i) { return i > -1; }).length;
    if (textIndicesFound > 1) {
        throw new CompositionXorItemsError([CellText, CellLink]);
    }
    // text must be used in CellText wrapper
    if (unwrappedTextIndex > -1) {
        throw new CompositionInvalidChildError(all[unwrappedTextIndex], 'text must be wrapped in CellText');
    }
    // it may contain up to 1 CellTexts/CellLinks/CellIcons/CellImages at the most each
    try {
        expect(foundTexts).toHaveMaxLength(1);
    }
    catch (_a) {
        throw new CompositionTooManyItemsError(CellText, 'one at most');
    }
    try {
        expect(foundLinks).toHaveMaxLength(1);
    }
    catch (_b) {
        throw new CompositionTooManyItemsError(CellLink, 'one at most');
    }
    try {
        expect(foundIcons).toHaveMaxLength(1);
    }
    catch (_c) {
        throw new CompositionTooManyItemsError(CellIcon, 'one at most');
    }
    try {
        expect(foundBaseImages).toHaveMaxLength(1);
    }
    catch (_d) {
        throw new CompositionTooManyItemsError(CellImageBase, 'one at most');
    }
    try {
        expect(foundSmallImages).toHaveMaxLength(1);
    }
    catch (_e) {
        throw new CompositionTooManyItemsError(CellImageSmall, 'one at most');
    }
    try {
        expect(foundLargeImages).toHaveMaxLength(1);
    }
    catch (_f) {
        throw new CompositionTooManyItemsError(CellImageLarge, 'one at most');
    }
};
var validateContentCellOrder = function (engine) {
    var expect = engine.expect, getAll = engine.getAll, flatIndexOfByKind = engine.flatIndexOfByKind;
    var all = getAll();
    var textIndex = flatIndexOfByKind(CellText);
    var linkIndex = flatIndexOfByKind(CellLink);
    var iconIndex = flatIndexOfByKind(CellIcon);
    var baseImageIndex = flatIndexOfByKind(CellImageBase);
    var smallImageIndex = flatIndexOfByKind(CellImageSmall);
    var largeImageIndex = flatIndexOfByKind(CellImageLarge);
    // if CellIcons or CellImages (Base, Small, Large) are provided, they must come before CellText
    if (iconIndex > -1 && textIndex > -1) {
        try {
            expect(iconIndex).toBeLessThan(textIndex);
        }
        catch (_a) {
            throw new CompositionOrderError(all[iconIndex], all[textIndex]);
        }
    }
    if (baseImageIndex > -1 && textIndex > -1) {
        try {
            expect(baseImageIndex).toBeLessThan(textIndex);
        }
        catch (_b) {
            throw new CompositionOrderError(all[baseImageIndex], all[textIndex]);
        }
    }
    if (smallImageIndex > -1 && textIndex > -1) {
        try {
            expect(smallImageIndex).toBeLessThan(textIndex);
        }
        catch (_c) {
            throw new CompositionOrderError(all[smallImageIndex], all[textIndex]);
        }
    }
    if (largeImageIndex > -1 && textIndex > -1) {
        try {
            expect(largeImageIndex).toBeLessThan(textIndex);
        }
        catch (_d) {
            throw new CompositionOrderError(all[largeImageIndex], all[textIndex]);
        }
    }
    // if CellIcons or CellImages (Base, Small, Large) are provided, they must come before CellLink
    if (iconIndex > -1 && linkIndex > -1) {
        try {
            expect(iconIndex).toBeLessThan(linkIndex);
        }
        catch (_e) {
            throw new CompositionOrderError(all[iconIndex], all[linkIndex]);
        }
    }
    if (baseImageIndex > -1 && linkIndex > -1) {
        try {
            expect(baseImageIndex).toBeLessThan(linkIndex);
        }
        catch (_f) {
            throw new CompositionOrderError(all[baseImageIndex], all[linkIndex]);
        }
    }
    if (smallImageIndex > -1 && linkIndex > -1) {
        try {
            expect(smallImageIndex).toBeLessThan(linkIndex);
        }
        catch (_g) {
            throw new CompositionOrderError(all[smallImageIndex], all[linkIndex]);
        }
    }
    if (largeImageIndex > -1 && linkIndex > -1) {
        try {
            expect(largeImageIndex).toBeLessThan(linkIndex);
        }
        catch (_h) {
            throw new CompositionOrderError(all[largeImageIndex], all[linkIndex]);
        }
    }
};

var CONTENT_CELL_GUTTER_PX = 10;
var validator$3 = new CompositionValidator([validateContentCellNumber, validateContentCellOrder]);
var ContentCell = function (_a) {
    var children = _a.children, rest = __rest(_a, ["children"]);
    validator$3.validate(children);
    return (jsx(CellBase, __assign({ gutterPx: CONTENT_CELL_GUTTER_PX }, rest, { children: children })));
};
ContentCell.displayName = 'ContentCell';

var HeaderCell = function (props) {
    return jsx(HeaderCellBase, __assign({}, props));
};
HeaderCell.displayName = 'HeaderCell';

var getPercentLabel = function (percent, suffix) {
    if (suffix === void 0) { suffix = ''; }
    var num = Math.round((percent + Number.EPSILON) * 100);
    return "".concat(num, "% ").concat(suffix).trim();
};
var CellPercentLabel = function (_a) {
    var percent = _a.percent, suffix = _a.suffix;
    var label = getPercentLabel(percent, suffix);
    return jsx(CellText, __assign({ title: label }, { children: label }));
};

var PROGRESS_BAR_WIDTH = 40;
var heightStyle = { height: spacing.s8 };
var getStyles$f = function (theme, percent) {
    return ({
        barBase: __assign({ fill: theme.colors.systemGrayscale20, width: PROGRESS_BAR_WIDTH }, heightStyle),
        bar: __assign(__assign({ width: "calc(100% * ".concat(percent, ")"), transition: '0.35s width', fill: theme.colors.systemGrayscale50 }, heightStyle), { rx: '4px' }),
        container: __assign({ width: PROGRESS_BAR_WIDTH, borderRadius: spacing.s8 }, heightStyle),
    });
};
var CellProgressBar = function (_a) {
    var styles = _a.styles, percent = _a.percent;
    var theme = useTheme();
    var _b = combineStyles(getStyles$f(theme, percent), styles), barBase = _b.barBase, bar = _b.bar, container = _b.container;
    var ariaLoading = useAriaLoading({ currentProgressValue: percent * 100 });
    return (jsxs("svg", __assign({ css: container }, ariaLoading, { children: [jsx("rect", { css: barBase }), jsx("rect", { css: bar, "data-testid": "progress-bar" })] })));
};

var PROGRESS_RING_SIZE = 24;
var RADIUS = 10;
var CIRCUMFERENCE = RADIUS * 2 * Math.PI;
var svgCircleCss = {
    strokeWidth: 3,
    strokeDasharray: "".concat(CIRCUMFERENCE, ",").concat(CIRCUMFERENCE),
    fill: 'transparent',
    r: RADIUS,
    cx: 12,
    cy: 12,
};
var getStyles$e = function (theme, p) {
    return ({
        ring: __assign({ strokeDashoffset: CIRCUMFERENCE * (1 - p), transition: '0.35s stroke-dashoffset', transform: 'rotate(-90deg)', transformOrigin: '50% 50%', stroke: theme.colors.systemGrayscale50 }, svgCircleCss),
        ringBase: __assign({ stroke: theme.colors.systemGrayscale20 }, svgCircleCss),
        container: {
            width: PROGRESS_RING_SIZE,
            height: PROGRESS_RING_SIZE,
        },
    });
};
var CellProgressRing = function (_a) {
    var styles = _a.styles, percent = _a.percent;
    var theme = useTheme();
    var _b = combineStyles(getStyles$e(theme, percent), styles), ringBase = _b.ringBase, ring = _b.ring, container = _b.container;
    var ariaLoading = useAriaLoading({ currentProgressValue: percent * 100 });
    return (jsxs("svg", __assign({ css: container }, ariaLoading, { children: [jsx("circle", { css: ringBase }), jsx("circle", { css: ring, "data-testid": "progress-ring" })] })));
};

var validateProgressCellNumber = function (engine) {
    var expect = engine.expect, findAllByKind = engine.findAllByKind, flatIndexOfByKind = engine.flatIndexOfByKind;
    var textIndex = flatIndexOfByKind(CellText);
    var percentLabelIndex = flatIndexOfByKind(CellPercentLabel);
    var ringIndex = flatIndexOfByKind(CellProgressRing);
    var barIndex = flatIndexOfByKind(CellProgressBar);
    var foundTexts = findAllByKind(CellText);
    var foundPercentLabels = findAllByKind(CellPercentLabel);
    var foundRings = findAllByKind(CellProgressRing);
    var foundBars = findAllByKind(CellProgressBar);
    // only one of types CellProgressRing or CellProgressBar may be used
    var graphicIndicesFound = [ringIndex, barIndex].filter(function (i) { return i > -1; }).length;
    if (graphicIndicesFound > 1) {
        throw new CompositionXorItemsError([CellProgressRing, CellProgressBar]);
    }
    // only one of types CellText or CellPercentLabel may be used
    var textIndicesFound = [textIndex, percentLabelIndex].filter(function (i) { return i > -1; }).length;
    if (textIndicesFound > 1) {
        throw new CompositionXorItemsError([CellText, CellPercentLabel]);
    }
    // it may contain up to 1 CellText/CellPercentLabel/CellProgressRing/CellProgressBar at the most each
    try {
        expect(foundTexts).toHaveMaxLength(1);
    }
    catch (_a) {
        throw new CompositionTooManyItemsError(CellText, 'one at most');
    }
    try {
        expect(foundPercentLabels).toHaveMaxLength(1);
    }
    catch (_b) {
        throw new CompositionTooManyItemsError(CellPercentLabel, 'one at most');
    }
    try {
        expect(foundRings).toHaveMaxLength(1);
    }
    catch (_c) {
        throw new CompositionTooManyItemsError(CellProgressRing, 'one at most');
    }
    try {
        expect(foundBars).toHaveMaxLength(1);
    }
    catch (_d) {
        throw new CompositionTooManyItemsError(CellProgressBar, 'one at most');
    }
};
var validateProgressCellOrder = function (engine) {
    var expect = engine.expect, getAll = engine.getAll, flatIndexOfByKind = engine.flatIndexOfByKind;
    var all = getAll();
    var textIndex = flatIndexOfByKind(CellText);
    var percentLabelIndex = flatIndexOfByKind(CellPercentLabel);
    var ringIndex = flatIndexOfByKind(CellProgressRing);
    var barIndex = flatIndexOfByKind(CellProgressBar);
    // if CellProgressRing is provided, it must come before CellPercentLabel
    if (ringIndex > -1 && percentLabelIndex > -1) {
        try {
            expect(ringIndex).toBeLessThan(percentLabelIndex);
        }
        catch (_a) {
            throw new CompositionOrderError(all[ringIndex], all[percentLabelIndex]);
        }
    }
    // if CellProgressRing is provided, CellPercentLabel must also be
    if (ringIndex > -1 && percentLabelIndex === -1) {
        throw new CompositionTooFewItemsError(CellPercentLabel, 'one at most');
    }
    // if CellProgressRing is provided, CellText must never be
    if (ringIndex > -1 && textIndex > -1) {
        throw new CompositionTooManyItemsError(CellText, 'exactly zero');
    }
    // if CellProgressBar is provided, it must come before CellText
    if (barIndex > -1 && textIndex > -1) {
        try {
            expect(barIndex).toBeLessThan(textIndex);
        }
        catch (_b) {
            throw new CompositionOrderError(all[barIndex], all[textIndex]);
        }
    }
    // if CellProgressBar is provided, CellText must also be
    if (barIndex > -1 && textIndex === -1) {
        throw new CompositionTooFewItemsError(CellText, 'one at most');
    }
    // if CellProgressBar is provided, CellPercentLabel must never be
    if (barIndex > -1 && percentLabelIndex > -1) {
        throw new CompositionTooManyItemsError(CellPercentLabel, 'exactly zero');
    }
};

var validator$2 = new CompositionValidator([validateProgressCellNumber, validateProgressCellOrder]);
var ProgressCell = function (_a) {
    var children = _a.children, rest = __rest(_a, ["children"]);
    validator$2.validate(children);
    return jsx(CellBase, __assign({}, rest, { children: children }));
};
ProgressCell.displayName = 'ProgressCell';

var getStyles$d = function () { return ({
    container: {
        verticalAlign: 'middle',
    },
}); };
var CellSwitch = forwardRef(function CellSwitch(props, ref) {
    var styles = getStyles$d();
    return jsx(SwitchBase, __assign({ ref: ref }, props, { styles: styles }));
});
CellSwitch.displayName = 'CellSwitch';

var getStyles$c = function () { return ({
    container: {
        verticalAlign: 'middle',
        marginRight: 0,
    },
}); };
var CellCheckbox = forwardRef(function CellCheckbox(props, ref) {
    var styles = getStyles$c();
    return jsx(CheckboxBase, __assign({ ref: ref }, props, { styles: styles }));
});
CellCheckbox.displayName = 'CellCheckbox';

var validateSelectionCell = function (engine) {
    var expect = engine.expect, findAllByKind = engine.findAllByKind;
    var allCheckboxes = findAllByKind(CellCheckbox);
    var allSwitches = findAllByKind(CellSwitch);
    // It can contain up to one CellCheckbox
    try {
        expect(allCheckboxes).toHaveMaxLength(1);
    }
    catch (_a) {
        var addtl = 'up to one';
        throw new CompositionTooManyItemsError(CellCheckbox, addtl);
    }
    // It can contain up to one CellSwitch
    try {
        expect(allSwitches).toHaveMaxLength(1);
    }
    catch (_b) {
        var addtl = 'up to one';
        throw new CompositionTooManyItemsError(CellSwitch, addtl);
    }
    // It can contain CellCheckbox or CellSwitch, but not both
    var bothExist = allCheckboxes.length > 0 && allSwitches.length > 0;
    if (bothExist) {
        throw new CompositionXorItemsError([CellSwitch, CellSwitch]);
    }
};

var TABLE_SELECTION_GUTTER = 0;
var getStyles$b = function (alignment) { return ({
    content: {
        paddingRight: alignment === 'right' ? spacing.s16 : spacing.s8,
    },
}); };
var validator$1 = new CompositionValidator([validateSelectionCell]);
var SelectionCell = function (props) {
    var alignment = props.alignment, children = props.children;
    var styles = getStyles$b(alignment);
    validator$1.validate(children);
    return (jsx(CellBase, __assign({}, props, { styles: styles, gutterPx: TABLE_SELECTION_GUTTER }, { children: children })));
};
SelectionCell.displayName = 'SelectionCell';

var CellApprovedIcon = function (props) { return (jsx(CellIcon, __assign({ "aria-hidden": true, component: jsx(ApprovedIcon, {}), color: "brandPrimaryRegular", "data-testid": "approved-icon" }, props))); };

var CellDefaultIcon = function (props) { return (jsx(CellIcon, __assign({ "aria-hidden": true, component: jsx(InformationIcon, {}), color: "brandSecondaryRegular", "data-testid": "default-icon" }, props))); };

var CellInfoIcon = function (props) { return (jsx(CellIcon, __assign({ "aria-hidden": true, component: jsx(InformationIcon, {}), color: "brandHighlightRegular", "data-testid": "info-icon" }, props))); };

var CellIssueIcon = function (props) { return (jsx(CellIcon, __assign({ "aria-hidden": true, component: jsx(IssueIcon, {}), color: "systemDetrimentalRegular", "data-testid": "issue-icon" }, props))); };

var validateStatusCellNumber = function (engine) {
    var expect = engine.expect, findAllByKind = engine.findAllByKind, flatIndexOfByKind = engine.flatIndexOfByKind;
    var approvedIndex = flatIndexOfByKind(CellApprovedIcon);
    var issueIndex = flatIndexOfByKind(CellIssueIcon);
    var infoIndex = flatIndexOfByKind(CellInfoIcon);
    var defaultIndex = flatIndexOfByKind(CellDefaultIcon);
    var foundTexts = findAllByKind(CellText);
    var foundApprovedIcons = findAllByKind(CellApprovedIcon);
    var foundIssueIcons = findAllByKind(CellIssueIcon);
    var foundInfoIcons = findAllByKind(CellInfoIcon);
    var foundDefaultIcons = findAllByKind(CellDefaultIcon);
    // it must contain only one Icon type
    var iconIndicesFound = [approvedIndex, issueIndex, infoIndex, defaultIndex].filter(function (i) { return i > -1; }).length;
    if (iconIndicesFound > 1 || iconIndicesFound < 1) {
        throw new CompositionXorItemsError([
            CellApprovedIcon,
            CellIssueIcon,
            CellInfoIcon,
            CellDefaultIcon,
        ]);
    }
    // it must contain 1 CellText
    try {
        expect(foundTexts).toHaveLength(1);
    }
    catch (_a) {
        if (foundTexts.length > 1) {
            throw new CompositionTooManyItemsError(CellText, 'exactly one');
        }
        if (foundTexts.length < 1) {
            throw new CompositionTooFewItemsError(CellText, 'exactly one');
        }
    }
    // it may contain no more than 1 Icon types at the most each
    try {
        expect(foundApprovedIcons).toHaveMaxLength(1);
    }
    catch (_b) {
        throw new CompositionTooManyItemsError(CellApprovedIcon, 'one at most');
    }
    try {
        expect(foundIssueIcons).toHaveMaxLength(1);
    }
    catch (_c) {
        throw new CompositionTooManyItemsError(CellIssueIcon, 'one at most');
    }
    try {
        expect(foundInfoIcons).toHaveMaxLength(1);
    }
    catch (_d) {
        throw new CompositionTooManyItemsError(CellInfoIcon, 'one at most');
    }
    try {
        expect(foundDefaultIcons).toHaveMaxLength(1);
    }
    catch (_e) {
        throw new CompositionTooManyItemsError(CellDefaultIcon, 'one at most');
    }
};
var validateStatusCellOrder = function (engine) {
    var expect = engine.expect, getAll = engine.getAll, flatIndexOfByKind = engine.flatIndexOfByKind;
    var all = getAll();
    var textIndex = flatIndexOfByKind(CellText);
    var approvedIndex = flatIndexOfByKind(CellApprovedIcon);
    var issueIndex = flatIndexOfByKind(CellIssueIcon);
    var infoIndex = flatIndexOfByKind(CellInfoIcon);
    var defaultIndex = flatIndexOfByKind(CellDefaultIcon);
    // Icons must come before CellText
    // CellApprovedIcon
    try {
        expect(approvedIndex).toBeLessThan(textIndex);
    }
    catch (_a) {
        throw new CompositionOrderError(all[approvedIndex], all[textIndex]);
    }
    // CellIssueIcon
    try {
        expect(issueIndex).toBeLessThan(textIndex);
    }
    catch (_b) {
        throw new CompositionOrderError(all[issueIndex], all[textIndex]);
    }
    // CellInfoIcon
    try {
        expect(infoIndex).toBeLessThan(textIndex);
    }
    catch (_c) {
        throw new CompositionOrderError(all[infoIndex], all[textIndex]);
    }
    // CellDefaultIcon
    try {
        expect(defaultIndex).toBeLessThan(textIndex);
    }
    catch (_d) {
        throw new CompositionOrderError(all[defaultIndex], all[textIndex]);
    }
};

var STATUS_LABEL_ID = 'status-cell-label';
var STATUS_CELL_GUTTER_PX = 10;
var validator = new CompositionValidator([validateStatusCellNumber, validateStatusCellOrder]);
var StatusCell = function (_a) {
    var children = _a.children, rest = __rest(_a, ["children"]);
    validator.validate(children);
    return (jsx(CellBase, __assign({}, rest, { gutterPx: STATUS_CELL_GUTTER_PX }, { children: children })));
};
StatusCell.displayName = 'StatusCell';

var hintId = function (inputId) { return "".concat(inputId, "-hint"); };
var errorId = function (inputId) { return "".concat(inputId, "-error"); };

var getStyles$a = function (theme) {
    return ({
        container: {
            display: 'flex',
            alignItems: 'center',
            gap: spacing.s4,
        },
        icon: {
            flex: 'none',
        },
        inputDescriptionError: __assign(__assign({}, theme.typography.bodyMedium2), { color: theme.colors.systemDetrimentalRegular, margin: 0, wordBreak: 'break-word' }),
    });
};
function InputError(_a) {
    var inputId = _a.inputId, error = _a.error;
    var theme = useTheme();
    var styles = getStyles$a(theme);
    return (jsxs("div", __assign({ css: styles.container }, { children: [jsx(IssueIcon, { size: 16, color: "systemDetrimentalRegular", css: styles.icon }), jsx(InputDescriptionErrorText, __assign({ id: errorId(inputId), css: styles.inputDescriptionError }, { children: error }))] })));
}

function useStyles$1(_a) {
    var disabled = _a.disabled;
    var theme = useTheme();
    return {
        inputDescriptionHint: __assign(__assign({}, theme.typography.bodySmall2), { color: disabled ? theme.colors.systemGrayscale30 : theme.colors.systemGrayscale50, margin: 0 }),
    };
}
function InputHint(_a) {
    var inputId = _a.inputId, hint = _a.hint, disabled = _a.disabled;
    var styles = useStyles$1({ disabled: disabled });
    return (jsx(InputDescriptionHintText, __assign({ id: hintId(inputId), css: styles.inputDescriptionHint, disabled: disabled }, { children: hint })));
}

var useStyles = function (_a) {
    var hideLabel = _a.hideLabel;
    var theme = useTheme();
    var inputWrapper = {
        container: {
            display: 'block',
            marginTop: spacing.s4,
        },
    };
    return {
        labelContainer: {
            display: 'flex',
            gap: spacing.s4,
            alignItems: 'center',
            margin: 0,
        },
        field: {
            display: 'block',
            label: __assign(__assign({}, theme.typography.bodyMedium1), { display: hideLabel ? 'none' : 'block', width: 'auto', height: 'auto', margin: 0, textAlign: 'left' }),
        },
        errorContainer: {
            marginTop: spacing.s4,
        },
        inputWrapper: inputWrapper,
    };
};
function InputFieldContainer(_a) {
    var isOptional = _a.isOptional, label = _a.label, hint = _a.hint, id = _a.id, error = _a.error, children = _a.children, hideLabel = _a.hideLabel, disabled = _a.disabled;
    var styles = useStyles({ hideLabel: hideLabel });
    return (jsxs(Field, __assign({ css: styles.field }, { children: [jsx("div", __assign({ css: styles.labelContainer }, { children: jsx(Label, __assign({ htmlFor: id, isOptional: isOptional, disabled: disabled }, { children: label })) })), hint && jsx(InputHint, { inputId: id, hint: hint, disabled: disabled }), jsxs(InputWrapper, __assign({ styles: styles.inputWrapper }, { children: [children, error && (jsx("div", __assign({ css: styles.errorContainer }, { children: error && jsx(InputError, { inputId: id, error: error }) })))] }))] })));
}

var VerticalTextInputField = forwardRef(function VerticalTextInputField(_a, ref) {
    var id = _a.id, onChange = _a.onChange, error = _a.error, invalid = _a.invalid, isOptional = _a.isOptional, hideLabel = _a.hideLabel, disabled = _a.disabled, readOnly = _a.readOnly, rest = __rest(_a, ["id", "onChange", "error", "invalid", "isOptional", "hideLabel", "disabled", "readOnly"]);
    var theme = useTheme();
    return (jsx(InputFieldContainer, __assign({ id: id, error: error, isOptional: isOptional, hideLabel: hideLabel, disabled: disabled }, rest, { children: jsx(InputText, __assign({ ref: ref, id: id, onChange: onChange, invalid: invalid || !!error, disabled: disabled || readOnly, instructionInputTextId: rest.hint && hintId(id), style: {
                color: disabled ? theme.colors.systemGrayscale30 : theme.colors.systemGrayscale70,
            } }, rest)) })));
});

var GAP_TO_LEFT = '-30px';
var GAP_TO_RIGHT = '410px';
var beaconStylesByPosition = {
    topLeft: { left: GAP_TO_LEFT, alignItems: 'start' },
    middleLeft: { left: GAP_TO_LEFT, alignItems: 'center' },
    bottomLeft: { left: GAP_TO_LEFT, alignItems: 'end' },
    bottomRight: { left: GAP_TO_RIGHT, alignItems: 'end' },
    middleRight: { left: GAP_TO_RIGHT, alignItems: 'center' },
    topRight: { left: GAP_TO_RIGHT, alignItems: 'start' },
    hidden: { left: '0px', alignItems: 'center' },
};
var ripple = keyframes(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n    0% {\n      left: 5px;\n      top: 5px;\n      opcity: 75;\n      width: 0;\n      height: 0;\n    }\n    100% {\n      left: -20px;\n      top: -20px;\n      opacity: 0;\n      width: 48px;\n      height: 48px;\n    }\n "], ["\n    0% {\n      left: 5px;\n      top: 5px;\n      opcity: 75;\n      width: 0;\n      height: 0;\n    }\n    100% {\n      left: -20px;\n      top: -20px;\n      opacity: 0;\n      width: 48px;\n      height: 48px;\n    }\n "])));
function FeatureHighlightBeacon(_a) {
    var position = _a.position;
    if (position === 'hidden') {
        return null;
    }
    var styles = {
        position: 'relative',
        left: beaconStylesByPosition[position].left,
        '&:before': {
            content: '" "',
            position: 'absolute',
            zIndex: 2,
            left: '-8px',
            top: '-8px',
            width: '24px',
            height: '24px',
            backgroundColor: '#1f5a96',
            borderRadius: '50%',
        },
        '&:after': {
            content: '" "',
            position: 'absolute',
            zIndex: 1,
            left: '-8px',
            top: '-8px',
            width: '24px',
            height: '24px',
            backgroundColor: "#1f5a96",
            borderRadius: '50%',
            boxShadow: '0 0 10px rgba(0, 0, 0, 0.3) inset',
            animationName: ripple,
            animationDuration: "1s",
            animationTimingFunction: 'ease',
            animationDelay: '0s',
            animationIterationCount: 'infinite',
            animationDirection: 'normal',
        },
    };
    return jsx("div", { css: styles });
}
var templateObject_1;

var getStyles$9 = function (theme, position) {
    return ({
        baseCss: {
            position: 'relative',
            boxShadow: "0 0.5em 3em ".concat(theme.colors.systemGrayscale20),
            display: 'flex',
            width: '396px',
            alignItems: beaconStylesByPosition[position].alignItems,
            borderRadius: spacing.s12,
            backgroundColor: 'white',
        },
    });
};
function FeatureHighlightBase(_a) {
    var styles = _a.styles, _b = _a.position, position = _b === void 0 ? 'topLeft' : _b, children = _a.children, rest = __rest(_a, ["styles", "position", "children"]);
    var theme = useTheme();
    var baseCss = combineStyles(getStyles$9(theme, position), styles).baseCss;
    return (jsx("div", __assign({ css: baseCss }, rest, { children: children })));
}

var FeatureHighlightContext = createContext(undefined);
function useFeatureHighlightContext() {
    var context = useContext(FeatureHighlightContext);
    if (!context)
        throw new Error('useFeatureHighlightContext must be used within a FeatureHighlight');
    return context;
}

function FeatureHighlight(_a) {
    var _b = _a.beaconPosition, beaconPosition = _b === void 0 ? 'topLeft' : _b, illustration = _a.illustration, content = _a.content, navigation = _a.navigation, currentStep = _a.currentStep, setCurrentStep = _a.setCurrentStep, setIsOpen = _a.setIsOpen, totalSteps = _a.totalSteps, closeButton = _a.closeButton, styles = _a.styles;
    var contextValue = { currentStep: currentStep, setCurrentStep: setCurrentStep, setIsOpen: setIsOpen, totalSteps: totalSteps };
    return (jsx(FeatureHighlightContext.Provider, __assign({ value: contextValue }, { children: jsxs(FeatureHighlightBase, __assign({ position: beaconPosition, styles: { baseCss: styles } }, { children: [jsx(FeatureHighlightBeacon, { position: beaconPosition }), jsxs("div", __assign({ css: { width: '100%' } }, { children: [closeButton, illustration, content, navigation] }))] })) })));
}

var getStyles$8 = function (theme) {
    return ({
        divCss: __assign({ color: theme.colors.systemGrayscale70 }, theme.typography.bodyLarge2),
    });
};
function FeatureHighlightDescription(_a) {
    var styles = _a.styles, children = _a.children, rest = __rest(_a, ["styles", "children"]);
    var theme = useTheme();
    var divCss = combineStyles(getStyles$8(theme), styles).divCss;
    return (jsx("div", __assign({ css: divCss }, rest, { children: children })));
}

var getStyles$7 = function (theme) {
    return ({
        textCss: __assign(__assign({ display: 'block', color: theme.colors.systemGrayscale70 }, theme.typography.titleMedium), { marginBottom: spacing.s8 }),
    });
};
function FeatureHighlightTitle(_a) {
    var styles = _a.styles, children = _a.children, rest = __rest(_a, ["styles", "children"]);
    var theme = useTheme();
    var textCss = combineStyles(getStyles$7(theme), styles).textCss;
    return (jsx("span", __assign({ css: textCss }, rest, { children: children })));
}

var getStyles$6 = function () {
    return ({
        divCss: { padding: "".concat(spacing.s16, "px ").concat(spacing.s32, "px") },
    });
};
function FeatureHighlightContent(_a) {
    var styles = _a.styles, children = _a.children, rest = __rest(_a, ["styles", "children"]);
    var divCss = combineStyles(getStyles$6(), styles).divCss;
    return (jsx("div", __assign({ css: divCss }, rest, { children: children })));
}

var getStyles$5 = function (theme) {
    return ({
        spanCss: __assign(__assign({ display: 'block', color: theme.colors.systemGrayscale50 }, theme.typography.bodyMedium1), { marginBottom: spacing.s8 }),
    });
};
function FeatureHighlightProgressText(_a) {
    var children = _a.children, styles = _a.styles, rest = __rest(_a, ["children", "styles"]);
    var theme = useTheme();
    var spanCss = combineStyles(getStyles$5(theme), styles).spanCss;
    return (jsx("span", __assign({ css: spanCss }, rest, { children: children })));
}

var getStyles$4 = function () {
    return ({
        btnCss: { minWidth: '65px', height: '40px', marginRight: spacing.s24 },
    });
};
var FeatureHighlightNextButton = forwardRef(function FeatureHighlightNextButton(_a, ref) {
    var userProvidedOnClick = _a.onClick, children = _a.children, styles = _a.styles, accessibleLabel = _a.accessibleLabel, rest = __rest(_a, ["onClick", "children", "styles", "accessibleLabel"]);
    var btnCss = combineStyles(getStyles$4(), styles).btnCss;
    var _b = useFeatureHighlightContext(), currentStep = _b.currentStep, totalSteps = _b.totalSteps, setIsOpen = _b.setIsOpen, setCurrentStep = _b.setCurrentStep;
    return (jsx(PrimaryButton, __assign({ css: btnCss, onClick: function () {
            if (currentStep === totalSteps - 1) {
                setIsOpen(false);
            }
            else {
                setCurrentStep(function (s) { return s + 1; });
            }
            userProvidedOnClick === null || userProvidedOnClick === void 0 ? void 0 : userProvidedOnClick({ currentStep: currentStep, totalSteps: totalSteps, setIsOpen: setIsOpen, setCurrentStep: setCurrentStep });
        }, ref: ref, "aria-label": accessibleLabel }, rest, { children: children })));
});

var getStyles$3 = function () {
    return ({
        btnCss: {
            borderRadius: '100%',
            height: spacing.s40,
            width: spacing.s40,
            minWidth: spacing.s40,
            marginLeft: spacing.s24,
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
        },
    });
};
var FeatureHighlightPrevButton = forwardRef(function FeatureHighlightPrevButton(_a, ref) {
    var userProvidedOnClick = _a.onClick, children = _a.children, styles = _a.styles, accessibleLabel = _a.accessibleLabel, rest = __rest(_a, ["onClick", "children", "styles", "accessibleLabel"]);
    var _b = useFeatureHighlightContext(), currentStep = _b.currentStep, setCurrentStep = _b.setCurrentStep, setIsOpen = _b.setIsOpen, totalSteps = _b.totalSteps;
    var btnCss = combineStyles(getStyles$3(), styles).btnCss;
    return (jsx(SecondaryButton, __assign({ disabled: currentStep === 0, css: btnCss, onClick: function () {
            setCurrentStep(function (s) { return s - 1; });
            userProvidedOnClick === null || userProvidedOnClick === void 0 ? void 0 : userProvidedOnClick({ currentStep: currentStep, setCurrentStep: setCurrentStep, setIsOpen: setIsOpen, totalSteps: totalSteps });
        }, ref: ref, "aria-label": accessibleLabel }, rest, { children: children })));
});

var getStyles$2 = function (prevButton) {
    return ({
        divCss: {
            height: '76px',
            display: 'flex',
            justifyContent: prevButton ? 'space-between' : 'end',
            alignItems: 'center',
        },
    });
};
function FeatureHighlightNavigation(_a) {
    var styles = _a.styles, prevButton = _a.prevButton, nextButton = _a.nextButton, rest = __rest(_a, ["styles", "prevButton", "nextButton"]);
    var divCss = combineStyles(getStyles$2(prevButton), styles).divCss;
    return (jsxs("div", __assign({ css: divCss }, rest, { children: [prevButton, nextButton] })));
}

var getStyles$1 = function () {
    return ({
        container: {
            height: '150px',
            overflow: 'hidden',
            borderRadius: "".concat(spacing.s12, "px ").concat(spacing.s12, "px 0 0"),
        },
    });
};
function FeatureHighlightIllustration(_a) {
    var children = _a.children, styles = _a.styles;
    var container = combineStyles(getStyles$1(), styles).container;
    return jsx("div", __assign({ css: container }, { children: children }));
}

var getStyles = function () {
    return ({
        button: {
            cursor: 'pointer',
            position: 'absolute',
            right: spacing.s8,
            top: spacing.s8,
            borderRadius: '100%',
            height: spacing.s40,
            width: spacing.s40,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            alignContent: 'flexStart',
            flexDirection: 'column',
            background: 'white',
            border: '0px',
        },
        content: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
        },
    });
};
var FeatureHighlightCloseButton = forwardRef(function FeatureHighlightCloseButton(_a, ref) {
    var userProvidedOnClick = _a.onClick, accessibleLabel = _a.accessibleLabel, rest = __rest(_a, ["onClick", "accessibleLabel"]);
    var styles = getStyles();
    var _b = useFeatureHighlightContext(), currentStep = _b.currentStep, setCurrentStep = _b.setCurrentStep, setIsOpen = _b.setIsOpen, totalSteps = _b.totalSteps;
    var handleOnClick = function () {
        setIsOpen(false);
        userProvidedOnClick === null || userProvidedOnClick === void 0 ? void 0 : userProvidedOnClick({ currentStep: currentStep, setCurrentStep: setCurrentStep, setIsOpen: setIsOpen, totalSteps: totalSteps });
    };
    return (jsx(ButtonBase, __assign({ ref: ref, onClick: handleOnClick, styles: styles }, rest, { "aria-label": accessibleLabel }, { children: jsx(CloseIcon, { size: 24 }) })));
});

var index = /*#__PURE__*/Object.freeze({
    __proto__: null,
    FeatureHighlight: FeatureHighlight,
    FeatureHighlightDescription: FeatureHighlightDescription,
    FeatureHighlightTitle: FeatureHighlightTitle,
    FeatureHighlightContent: FeatureHighlightContent,
    FeatureHighlightProgressText: FeatureHighlightProgressText,
    FeatureHighlightNextButton: FeatureHighlightNextButton,
    FeatureHighlightPrevButton: FeatureHighlightPrevButton,
    FeatureHighlightNavigation: FeatureHighlightNavigation,
    FeatureHighlightIllustration: FeatureHighlightIllustration,
    FeatureHighlightCloseButton: FeatureHighlightCloseButton
});

export { ActionCell, Align, CIRCUMFERENCE, CellApprovedIcon, CellBase, CellButton, CellButtonBase, CellCheckbox, CellDefaultIcon, CellIcon, CellImageBase, CellImageLarge, CellImageSmall, CellInfoIcon, CellIssueIcon, CellLink, CellOverflowButton, CellPercentLabel, CellProgressBar, CellProgressRing, CellSwitch, CellText, ContentCell, DEFAULT_OPTIONAL_TEXT, Field, FieldBase, HeaderCell, HeaderCellBase, HeaderCheckbox, HeaderIcon, HeaderSortIcon, HeaderSubtitle, HeaderTitle, HeaderTitleText, IconButton, IconButtonBase, IconInputBase, InputCurrency, InputDescriptionBase, InputDescriptionErrorText, InputDescriptionHintText, InputPercent, InputSearch, InputText, InputWrapper, LABEL_WIDTH, Label, LabelBase, PrimaryButton, ProgressCell, STATUS_LABEL_ID, SecondaryButton, SelectionCell, SortState, StatusCell, TableWrapper, VerticalTextInputField, index as unstableFeatureHighlight, index$1 as unstableInfoInputIcon };
